/**
 * useAMClientDetail — Fetch all data for the Client 360° view
 *
 * Single hook that loads agent info, contracts, leads, messages,
 * and assignment details for one client.
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ClientDetail {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
  phone: string | null;
  vertical: string;
  tier: string;
  is_active: boolean;
  licensed_states: string[];
  wallet_balance: number | null;
  performance_score: number;
  performance_metrics: Record<string, unknown> | null;
  leads_received_this_month: number;
  leads_received_today: number;
  assigned_at: string | null;
}

export interface ClientContract {
  id: string;
  contract_type: string;
  status: string;
  lead_types: string[] | null;
  leads_delivered: number;
  total_leads_committed: number;
  cycle_start: number;
  cycle_end: number;
  paused_at: string | null;
  paused_reason: string | null;
  cancel_at_period_end: boolean;
  priority_tier: string;
  created_at: string;
  notes: string | null;
}

export interface ClientLead {
  id: string;
  lead_type_name: string;
  state: string;
  status: string;
  disposition: string | null;
  estimated_value: number;
  charged_amount: number | null;
  created_at: number;
  agent_status: string;
  agent_contacted_at: number | null;
  lead_profile: Record<string, unknown> | null;
}

export interface ClientNote {
  id: string;
  am_id: string;
  agent_id: string;
  body: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
}

export function useAMClientDetail(agentId: string | undefined, amId: string | undefined) {
  const [agent, setAgent] = useState<ClientDetail | null>(null);
  const [contracts, setContracts] = useState<ClientContract[]>([]);
  const [leads, setLeads] = useState<ClientLead[]>([]);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [recentMessages, setRecentMessages] = useState<{ body: string; sender_role: string; created_at: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    if (!agentId || !amId) return;
    setLoading(true);
    setError(null);

    try {
      // Fetch agent info
      const { data: agentData, error: agentErr } = await supabase
        .from("agents")
        .select("id, name, company, email, phone, vertical, tier, is_active, licensed_states, wallet_balance, performance_score, performance_metrics, leads_received_this_month, leads_received_today")
        .eq("id", agentId)
        .single();

      if (agentErr) throw new Error("Failed to load client data");

      // Fetch assignment date
      const { data: assignment } = await supabase
        .from("client_am_assignments")
        .select("assigned_at")
        .eq("agent_id", agentId)
        .eq("am_id", amId)
        .eq("status", "active")
        .maybeSingle();

      setAgent({
        ...agentData,
        performance_metrics: agentData.performance_metrics as Record<string, unknown> | null,
        assigned_at: assignment?.assigned_at || null,
      });

      // Fetch contracts
      const { data: contractData } = await supabase
        .from("contracts")
        .select("id, contract_type, status, lead_types, leads_delivered, total_leads_committed, cycle_start, cycle_end, paused_at, paused_reason, cancel_at_period_end, priority_tier, created_at, notes")
        .eq("agent_id", agentId)
        .order("created_at", { ascending: false });

      setContracts((contractData || []) as ClientContract[]);

      // Fetch leads (last 100)
      const { data: leadData } = await supabase
        .from("leads")
        .select("id, lead_type_name, state, status, disposition, estimated_value, charged_amount, created_at, agent_status, agent_contacted_at, lead_profile")
        .eq("routed_to_agent_id", agentId)
        .order("created_at", { ascending: false })
        .limit(100);

      setLeads((leadData || []) as ClientLead[]);

      // Fetch recent messages (last 3)
      const { data: msgData } = await supabase
        .from("am_messages")
        .select("body, sender_role, created_at")
        .eq("am_id", amId)
        .eq("agent_id", agentId)
        .order("created_at", { ascending: false })
        .limit(3);

      setRecentMessages((msgData || []) as { body: string; sender_role: string; created_at: string }[]);

      // Fetch notes
      const { data: noteData } = await supabase
        .from("am_notes")
        .select("*")
        .eq("agent_id", agentId)
        .order("created_at", { ascending: false });

      setNotes((noteData || []) as unknown as ClientNote[]);
    } catch (err: any) {
      console.error("[useAMClientDetail] Error:", err);
      setError(err.message || "Failed to load client data");
    } finally {
      setLoading(false);
    }
  }, [agentId, amId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // CRUD for notes
  const addNote = useCallback(async (body: string) => {
    if (!agentId || !amId) return null;
    const { data, error } = await supabase
      .from("am_notes")
      .insert({ am_id: amId, agent_id: agentId, body })
      .select()
      .single();

    if (error) {
      console.error("[useAMClientDetail] Failed to add note:", error);
      return null;
    }
    setNotes((prev) => [data as unknown as ClientNote, ...prev]);
    return data;
  }, [agentId, amId]);

  const updateNote = useCallback(async (noteId: string, updates: { body?: string; pinned?: boolean }) => {
    const { error } = await supabase
      .from("am_notes")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", noteId);

    if (error) {
      console.error("[useAMClientDetail] Failed to update note:", error);
      return false;
    }
    setNotes((prev) =>
      prev.map((n) => (n.id === noteId ? { ...n, ...updates, updated_at: new Date().toISOString() } : n))
    );
    return true;
  }, []);

  const deleteNote = useCallback(async (noteId: string) => {
    const { error } = await supabase
      .from("am_notes")
      .delete()
      .eq("id", noteId);

    if (error) {
      console.error("[useAMClientDetail] Failed to delete note:", error);
      return false;
    }
    setNotes((prev) => prev.filter((n) => n.id !== noteId));
    return true;
  }, []);

  return {
    agent,
    contracts,
    leads,
    notes,
    recentMessages,
    loading,
    error,
    refetch: fetchAll,
    addNote,
    updateNote,
    deleteNote,
  };
}
